/**
 * 商品登録画面（§7.1・§8.4）：撮影→VLM抽出→ユーザー確認→保存。
 * VLM出力を無検証で採用しない。確認フォームを必ず経由する。
 * 写真はサーバでVLM推論後に即時破棄される（端末にも保存しない）。
 */
import type { Product, ScanResult } from "@uvk/shared";
import { notify, state } from "../state.js";
import { db } from "../db.js";
import { saveProductMeta, scanProduct } from "../api.js";
import { esc } from "../ui.js";

let scanning = false;
let pending: ScanResult | null = null;

export function renderProducts(el: HTMLElement): void {
  el.innerHTML = `
    <div class="card">
      <h2>🧴 日焼け止めを登録</h2>
      <p class="sub">パッケージを撮影するとSPF/PAを自動で読み取ります。写真は読み取り後すぐに破棄され、保存されません。</p>
      <input type="file" id="photo" accept="image/*" capture="environment" hidden />
      <div class="row">
        <button id="scan-btn" class="primary grow" ${scanning ? "disabled" : ""}>
          ${scanning ? "読み取り中…" : "📷 撮影して読み取る"}
        </button>
        <button id="manual-btn" class="ghost">手入力</button>
      </div>
    </div>

    <div class="card" id="confirm-card" ${pending ? "" : "hidden"}>
      <h2>✅ 読み取り結果の確認 <span class="sub">まちがっていたら直してください</span></h2>
      <label class="field">SPF（10〜50）</label>
      <div class="row">
        <input type="number" id="f-spf" min="10" max="50" value="${pending?.spf ?? 30}" class="grow" />
        <label><input type="checkbox" id="f-plus" ${pending?.plus ? "checked" : ""} /> 50+</label>
      </div>
      <label class="field">PA（+の数）</label>
      <select id="f-pa">
        ${[1, 2, 3, 4].map((n) => `<option value="${n}" ${pending?.pa === n ? "selected" : ""}>PA${"+".repeat(n)}</option>`).join("")}
      </select>
      <label class="field">剤型</label>
      <select id="f-form">
        <option value="">不明</option>
        ${["cream", "spray", "gel", "stick"].map((f) => `<option value="${f}" ${pending?.form === f ? "selected" : ""}>${{ cream: "クリーム", spray: "スプレー", gel: "ジェル", stick: "スティック" }[f]}</option>`).join("")}
      </select>
      <label class="field"><input type="checkbox" id="f-wp" ${pending?.waterproof ? "checked" : ""} /> ウォータープルーフ</label>
      <button id="save-btn" class="primary" style="width:100%;margin-top:10px">この内容で保存</button>
    </div>

    <div class="card">
      <h2>登録済み</h2>
      ${state.products.length === 0 ? `<p class="sub">まだ登録されていません</p>` : ""}
      ${state.products
        .map(
          (p) => `
        <div class="row" style="padding:6px 0;border-bottom:1px solid var(--line)">
          <span class="grow">${p.spf === 50 && p.spfPlus ? "SPF50+" : `SPF${p.spf}`} / PA${"+".repeat(p.pa)}${p.waterproof ? " / WP" : ""}${p.form ? ` / ${esc(p.form)}` : ""}</span>
          <button class="ghost" data-del="${esc(p.id)}">削除</button>
        </div>`,
        )
        .join("")}
    </div>
  `;

  const photoInput = el.querySelector<HTMLInputElement>("#photo")!;
  el.querySelector<HTMLButtonElement>("#scan-btn")!.onclick = () => photoInput.click();

  photoInput.onchange = async () => {
    const file = photoInput.files?.[0];
    if (!file) return;
    scanning = true;
    notify();
    const result = await scanProduct(file);
    scanning = false;
    // 読めなくても手入力へフォールバック（確認UIは共通）
    pending = result ?? { spf: 30, plus: false, pa: 3, waterproof: false, form: null };
    if (!result) alert("読み取れませんでした。手入力で登録してください");
    notify();
  };

  el.querySelector<HTMLButtonElement>("#manual-btn")!.onclick = () => {
    pending = { spf: 30, plus: false, pa: 3, waterproof: false, form: null };
    notify();
  };

  el.querySelector<HTMLButtonElement>("#save-btn")!.onclick = async () => {
    const spf = Number(el.querySelector<HTMLInputElement>("#f-spf")!.value);
    const pa = Number(el.querySelector<HTMLSelectElement>("#f-pa")!.value);
    if (!Number.isInteger(spf) || spf < 10 || spf > 50) {
      alert("SPFは10〜50で入力してください");
      return;
    }
    const product: Product = {
      id: crypto.randomUUID(),
      spf,
      spfPlus: el.querySelector<HTMLInputElement>("#f-plus")!.checked,
      pa,
      waterproof: el.querySelector<HTMLInputElement>("#f-wp")!.checked,
      form: (el.querySelector<HTMLSelectElement>("#f-form")!.value || null) as Product["form"],
      createdAt: Date.now(),
    };
    await db.put("products", product);
    state.products.unshift(product);
    // サーバへは商品メタのみ送信（§2）
    void saveProductMeta({
      id: product.id,
      spf: product.spf,
      plus: product.spfPlus,
      pa: product.pa,
      waterproof: product.waterproof,
      form: product.form,
    });
    pending = null;
    notify();
  };

  el.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((b) => {
    b.onclick = async () => {
      await db.delete("products", b.dataset.del!);
      state.products = state.products.filter((p) => p.id !== b.dataset.del);
      notify();
    };
  });
}
